// Which side a team played a given round on.
//
// Sides are picked after the veto, so team1 is NOT reliably the team that starts CT — it starts T
// in a good share of maps. Guessing from the round number alone therefore flips whole halves, and
// the view then draws the opponent's players as "ours".
//
// Sources, most trustworthy first:
//   1. the demo roster — every player's real side for this exact round, overtime included;
//   2. the round end's T-side team id — parsed from S32 on, absent before and on uploaded demos;
//   3. the round-number convention (team1 starts CT, MR12 halves, MR3 overtime) — a last resort.

import type { AnalysisMatch } from '../../services/AnalysisService';
import type { ParsedReplay } from '../MatchPage/Replay/replayParser';
import { team1IsCtInRound } from '../MatchPage2/matchStats';

export type Side = 'T' | 'CT';

// Side from the demo roster, or null when neither lineup can be found in it.
export const rosterSideForTeam = (match: AnalysisMatch, teamId: string, replay: ParsedReplay): Side | null => {
    const isTeam1 = match.team1Id === teamId;
    const ours = new Set(isTeam1 ? match.team1playerids : match.team2playerids);
    const theirs = new Set(isTeam1 ? match.team2playerids : match.team1playerids);
    // Positive = we look like the T side. Counting the opponent too keeps this right when one
    // lineup list is short a stand-in.
    let tScore = 0;
    for (const entry of replay.roster) {
        if (!entry) continue;
        const sign = entry.team === 'T' ? 1 : -1;
        if (ours.has(entry.steamId)) tScore += sign;
        else if (theirs.has(entry.steamId)) tScore -= sign;
    }
    return tScore > 0 ? 'T' : tScore < 0 ? 'CT' : null;
};

export const teamSideInRound = (
    match: AnalysisMatch,
    teamId: string,
    roundNumber: number,
    replay?: ParsedReplay,
): Side => {
    const fromRoster = replay ? rosterSideForTeam(match, teamId, replay) : null;
    if (fromRoster) return fromRoster;

    // A stale id (an upload renamed after parsing) matches neither team and falls through.
    const tSideTeamId = match.roundEnds?.[roundNumber]?.tSideTeamID;
    const otherTeamId = match.team1Id === teamId ? match.team2Id : match.team1Id;
    if (tSideTeamId && tSideTeamId === teamId) return 'T';
    if (tSideTeamId && tSideTeamId === otherTeamId) return 'CT';

    const team1Ct = team1IsCtInRound(roundNumber);
    return (match.team1Id === teamId) === team1Ct ? 'CT' : 'T';
};
